/**
 * Ganancia real de VES→CLP en el modal "Actualizar Parámetros".
 *
 * El campo "Comisión VES - CLP (%)" no es la ganancia de VES→CLP: se suma
 * (dividido por 100) a la tasa CLP→VES de la franja más grande, que ya trae
 * restada la comisión CLP→VES. La ganancia real queda, a grandes rasgos, en
 * `comisión VES-CLP − comisión CLP-VES`, y con 1,4 / 1,3 es ~0,07 %. Este
 * archivo hace la cuenta exacta para que el operador la vea antes de guardar.
 *
 * Replica la fórmula del backend (`mejorClpVes` / `mejorVesClp` en
 * `rates/franjas/franjas.calculo.ts`), con los mismos redondeos a 4 decimales.
 * Es solo informativo: no cambia ningún cálculo ni lo que se guarda.
 *
 * Mide la franja más grande (0 descuentos). Las franjas menores suman sus
 * descuentos encima, así que siempre ganan más que esta.
 */

/** Ganancia por debajo de esta (en %) se pinta como "justa". */
export const GANANCIA_JUSTA_PCT = 0.5;

/** Ganancia objetivo que se usa para la sugerencia del modal. */
export const GANANCIA_SUGERIDA_PCT = 1;

export type NivelGanancia = 'negativa' | 'justa' | 'ok';

export interface GananciaVesClp {
  /** VES por CLP sin comisión. */
  mid: number;
  /** Tasa CLP→VES de la franja más grande (VES por CLP). */
  tasaClpVes: number;
  /** Tasa VES→CLP de la franja más grande (VES por CLP; el cliente recibe VES ÷ tasa). */
  tasaVesClp: number;
  /** Ganancia de Aguacate sobre el mid, en %. Negativa = el cliente recibe más que el mid. */
  gananciaPct: number;
  nivel: NivelGanancia;
  /** Comisión VES-CLP que habría que cargar para ganar `GANANCIA_SUGERIDA_PCT`. */
  comisionParaSugerida: number;
}

const r4 = (n: number): number => Math.round(n * 10000) / 10000;

/**
 * Devuelve la ganancia, o `null` si falta algún número o no se puede usar.
 * Nunca lanza: si da null, el aviso simplemente no se pinta.
 */
export function gananciaVesClp(
  rateVesUsdt: number | null | undefined,
  usdClp: number | null | undefined,
  comisionClpVes: number | null | undefined,
  comisionVesClp: number | null | undefined,
): GananciaVesClp | null {
  const rate = Number(rateVesUsdt);
  const usd = Number(usdClp);
  const c = Number(comisionClpVes);
  const cv = Number(comisionVesClp);

  if (![rate, usd, c, cv].every((n) => isFinite(n))) return null;
  if (rate <= 0 || usd <= 0 || c < 0 || cv < 0) return null;

  const mid = rate / usd;
  const tasaClpVes = r4(mid * (1 - c / 100));
  const tasaVesClp = r4(tasaClpVes + cv / 100);
  if (tasaVesClp <= 0) return null;

  const gananciaPct = (1 - mid / tasaVesClp) * 100;
  const objetivo = mid / (1 - GANANCIA_SUGERIDA_PCT / 100);
  const comisionParaSugerida = Math.ceil((objetivo - tasaClpVes) * 100 * 100) / 100;

  return {
    mid,
    tasaClpVes,
    tasaVesClp,
    gananciaPct,
    nivel: gananciaPct < 0 ? 'negativa' : gananciaPct < GANANCIA_JUSTA_PCT ? 'justa' : 'ok',
    comisionParaSugerida,
  };
}

// ───────────────────────── Presentación (sin cálculo) ─────────────────────────

/** Extremos de la regla del modal, en %. */
export const REGLA_MIN_PCT = -1;
export const REGLA_MAX_PCT = 2;

/** Rótulo de la píldora: «buena», «justa» o «pérdida». */
export function etiquetaNivelGanancia(nivel: NivelGanancia): string {
  return nivel === 'negativa' ? 'pérdida' : nivel === 'justa' ? 'justa' : 'buena';
}

/** Posición (0–100 %) de un valor en la regla de −1 % a +2 %, recortada a los extremos. */
export function posicionEnRegla(pct: number): number {
  if (!isFinite(pct)) return 0;
  const p = ((pct - REGLA_MIN_PCT) / (REGLA_MAX_PCT - REGLA_MIN_PCT)) * 100;
  return Math.min(100, Math.max(0, p));
}

/** «0,07 %», «−1,25 %»: coma decimal, signo menos tipográfico y espacio antes del %. */
export function formatoPct(pct: number, decimales = 2): string {
  if (!isFinite(pct)) return '—';
  const abs = Math.abs(pct).toLocaleString('es-CL', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  const cero = Number(abs.replace(/\./g, '').replace(',', '.')) === 0;
  return `${pct < 0 && !cero ? '−' : ''}${abs} %`;
}
